import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import {
  MemberCoursesPlayedClient,
  SharedCoursesPlayedDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import publicAxiosInstance from '../services/publicAxiosInstance';
import { isValidGuid } from '../utils/validateGuidUtil';

// The member's own sharing switch goes through axiosInstance (it needs their token). The public
// read goes through publicAxiosInstance, so a stale session on the visitor's browser can never
// turn "open a shared list" into "sign in first".
const apiClient = new MemberCoursesPlayedClient(API_BASE_URL, axiosInstance);
const publicClient = new MemberCoursesPlayedClient(
  API_BASE_URL,
  publicAxiosInstance,
);

const MY_SHARE_KEY = ['myCoursesPlayedShare'];

// The public address of a shared list. Built from the page's own origin, so the link a member
// copies always points at the site they are using (local, staging or live).
export const sharedListUrl = (shareId: string): string =>
  `${window.location.origin}/shared/courses-played/${shareId.toLowerCase()}`;

// ---- The member's own sharing switch (signed in) ----------------------------------------------

// The member's share code, or null while their list is not shared.
export const useMyCoursesPlayedShare = (
  enabled: boolean,
): UseQueryResult<string | null> =>
  useQuery<string | null>({
    queryKey: MY_SHARE_KEY,
    queryFn: async () => {
      const response = await apiClient.getMyCoursesPlayedShare();
      return response?.data?.shareId ?? null;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

// Turns sharing on and resolves with the code. Idempotent on the backend: a member already
// sharing gets their existing code back, so a double tap never breaks a link already sent.
export const useShareMyCoursesPlayed = (): UseMutationResult<
  string | null,
  unknown,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.shareMyCoursesPlayed();
      return response?.data?.shareId ?? null;
    },
    onSuccess: (shareId) => {
      queryClient.setQueryData(MY_SHARE_KEY, shareId);
    },
  });
};

// Turns sharing off. The old link stops working at once.
export const useStopSharingMyCoursesPlayed = (): UseMutationResult<
  void,
  unknown,
  void
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.stopSharingMyCoursesPlayed();
    },
    onSuccess: () => {
      queryClient.setQueryData(MY_SHARE_KEY, null);
    },
  });
};

// ---- The public view (no sign-in) --------------------------------------------------------------

export type SharedListState =
  | { status: 'invalid' }
  | { status: 'loading' }
  | { status: 'notShared' }
  | { status: 'error' }
  | { status: 'ready'; list: SharedCoursesPlayedDTO };

// A shared list by its code, for anyone. The code comes straight from the URL, so it is checked
// before anything is sent: a malformed link answers "not a valid link" without a request, and a
// well-formed code nobody holds (never shared, or sharing switched off) answers "not shared".
// The endpoint is whitelisted in SecuredMiddleware, and the request goes through
// publicAxiosInstance, so neither a signed-out visitor nor one with an expired session is ever
// bounced to /login.
export const useSharedCoursesPlayed = (
  shareId: string | undefined,
): SharedListState => {
  const code = shareId?.trim() ?? '';
  const isValid = isValidGuid(code);

  const query = useQuery<SharedCoursesPlayedDTO | null>({
    queryKey: ['sharedCoursesPlayed', code.toLowerCase()],
    queryFn: async () => {
      try {
        const response = await publicClient.getSharedCoursesPlayed(code);
        return response?.data ?? null;
      } catch (error: unknown) {
        if ((error as { status?: number })?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: isValid,
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!isValid) {
    return { status: 'invalid' };
  }
  if (query.isLoading) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data == null) {
    return { status: 'notShared' };
  }
  return { status: 'ready', list: query.data };
};
